import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Select,
  InputNumber,
  Button,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  message,
  Spin,
  Modal,
  Alert,
} from "antd";
import { Gauge } from "@ant-design/plots";
import dayjs from "dayjs";
import { useAppAuth } from "../../../context/use-app-auth";
import { useTranslation } from "react-i18next";
import requets from "../../../api";

const { Text } = Typography;

export const Thresholds = ({ device, lastMeasurement, loadingMeasurement }) => {
  const auth = useAppAuth();
  const req = useMemo(() => requets(auth?.user?.access_token), [auth?.user?.access_token]);
  const { t } = useTranslation("global");

  const [loading, setLoading] = useState(false);

  const [variable, setVariable] = useState(null);
  const [max, setMax] = useState(100);
  const [thresholds, setThresholds] = useState([]);
  const [existingConfig, setExistingConfig] = useState(null);
  const [noConfigFound, setNoConfigFound] = useState(false);

  const variablesData = useMemo(
    () =>
      lastMeasurement.map((item) => ({
        ...item,
        tag: `device_frmpayload_data_${item.field}`,
        displayName: item.field.replace(/_/g, " "),
      })),
    [lastMeasurement]
  );

  const handleReset = () => {
    setVariable(null);
    setMax(100);
    setThresholds([]);
    message.info(t("thresholds.messages.configCleared"), 6);
  };

  useEffect(() => {
    if (!device?.devEui) return;
    setVariable(null);
    setMax(100);
    setThresholds([]);
  }, [device]);

  useEffect(() => {
    if (!device?.devEui || !variable) {
      setExistingConfig(null);
      setNoConfigFound(false);
      return;
    }

    const fetchExistingConfig = async () => {
      const currentVar = variablesData.find((v) => v.field === variable);
      const tag = currentVar?.tag || `device_frmpayload_data_${variable}`;
      try {
        const response = await req.get(
          `/setting-threshold/search?devEui=${device.devEui}&measurementName=${tag}`,
        );
        if (response.data && response.data.length > 0) {
          const config = response.data[0];
          setExistingConfig(config);
          setNoConfigFound(false);
          if (config.jsonConfig) {
            setMax(config.jsonConfig.max || 100);
            setThresholds(config.jsonConfig.umbrales || []);
          }
        } else {
          setExistingConfig(null);
          setNoConfigFound(true);
          setMax(100);
          setThresholds([]);
        }
      } catch (error) {
        setExistingConfig(null);
        setMax(100);
        setThresholds([]);
        if (error?.response?.status === 404) {
          setNoConfigFound(true);
        } else {
          setNoConfigFound(false);
          console.error("[Thresholds] fetch config error:", error);
        }
      }
    };

    fetchExistingConfig();
  }, [device, variable, req, variablesData]);

  const currentMetric = useMemo(() => {
    return (
      variablesData.find((v) => v.field === variable) || {
        value: 0,
        unit: "",
      }
    );
  }, [variablesData, variable]);

  const handleSaveConfig = () => {
    if (!variable)
      return message.warning(t("thresholds.messages.selectVariableWarning"), 6);

    // MEJORA: Validación de que exista al menos un umbral agregado
    if (thresholds.length === 0) {
      return message.warning(
        "Debe agregar al menos un umbral al gráfico antes de guardar la configuración.",
        6,
      );
    }

    const hasEmptyValue = thresholds.some(
      (t) => t.value === null || t.value === undefined || t.value === "",
    );
    const hasInvalidValue = thresholds.some((t) => Number(t.value) <= 0);
    const hasExceedingMax = thresholds.some((t) => Number(t.value) > max);

    const values = thresholds.map((t) => Number(t.value));
    const hasDuplicateValues = values.some(
      (val, index) => values.indexOf(val) !== index,
    );

    const colors = thresholds.map((t) => t.color.toLowerCase());
    const hasDuplicateColors = colors.some(
      (col, index) => colors.indexOf(col) !== index,
    );

    if (hasEmptyValue)
      return message.error(t("thresholds.messages.emptyFieldsError"), 6);
    if (hasInvalidValue)
      return message.error(t("thresholds.messages.negativeValueError"), 6);
    if (hasExceedingMax)
      return message.error(t("thresholds.messages.maxLimitError", { max }), 6);
    if (hasDuplicateValues)
      return message.error(t("thresholds.messages.duplicateValueError"), 6);
    if (hasDuplicateColors)
      return message.error(t("thresholds.messages.duplicateColorError"), 6);

    const configPayload = {
      deviceUuid: device?.uuid,
      measurementUuid: currentMetric?.measurementUuid || currentMetric?.uuid,
      minValue: 0,
      maxValue: max,
      isActive: true,
      jsonConfig: {
        variable: variable,
        max: max,
        umbrales: thresholds,
      },
    };

    const saveConfig = async () => {
      setLoading(true);
      try {
        if (existingConfig?.uuid) {
          await req.put(`/setting-threshold/${existingConfig.uuid}`, configPayload);
          message.success(
            t("thresholds.messages.updateSuccess", { device: device?.name }) || "Configuración actualizada correctamente",
            6,
          );
        } else {
          await req.post("/setting-threshold", configPayload);
          message.success(
            t("thresholds.messages.saveSuccess", { device: device?.name }),
            6,
          );
        }
        // Refetch to get the new UUID or updated state
        try {
          const currentVar = variablesData.find((v) => v.field === variable);
          const tag = currentVar?.tag || `device_frmpayload_data_${variable}`;
          const response = await req.get(
            `/setting-threshold/search?devEui=${device.devEui}&measurementName=${tag}`,
          );
          if (response.data && response.data.length > 0) {
            setExistingConfig(response.data[0]);
          }
        } catch {
          // refetch failed — no problem, the save already succeeded
        }
      } catch (error) {
        console.error("[Thresholds] save error:", error);
        message.error("Error al guardar la configuración", 6);
      } finally {
        setLoading(false);
      }
    };

    saveConfig();
  };

  const gaugeConfig = useMemo(() => {
    const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value);
    const ticks = [0];
    const colors = [];

    sortedThresholds.forEach((t) => {
      const normalizedValue = t.value / max;
      if (normalizedValue > 0 && normalizedValue <= 1) {
        ticks.push(normalizedValue);
        colors.push(t.color);
      }
    });

    if (ticks[ticks.length - 1] < 1) {
      ticks.push(1);
      colors.push("#F0F0F0");
    }

    return {
      percent: max > 0 ? currentMetric.value / max : 0,
      range: { ticks: ticks, color: colors },
      axis: {
        label: { formatter: (v) => (Number(v) * max).toFixed(0) },
        subTickLine: { count: 3 },
      },
      indicator: {
        pointer: {
          style: {
            stroke: "#FF8C00",
            lineWidth: 4,
          },
        },
        pin: {
          style: {
            stroke: "#FF8C00",
            lineWidth: 3,
            fill: "#FFFFFF",
            r: 6,
          },
        },
      },
      statistic: {
        content: {
          formatter: () => `${currentMetric.value} ${currentMetric.unit}`,
          style: {
            fontSize: "24px",
            fontWeight: "bold",
            color: "#303133",
            lineHeight: "1.2",
            dominantBaseline: "alphabetical",
          },
        },
      },
      animation: { appear: { animation: "wave-in", duration: 1200 } },
    };
  }, [currentMetric, max, thresholds]);

  return (
    <Card>
      <Row gutter={[32, 32]}>
        <Col xs={24} md={10}>
          <Spin spinning={loading || loadingMeasurement}>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <div>
                <Text strong>{t("thresholds.variableToMeasure")}</Text>
                <Select
                  placeholder={t("thresholds.chooseVariable")}
                  style={{ width: "100%", marginTop: 8 }}
                  value={variable}
                  onChange={setVariable}
                  options={variablesData.map((v) => ({
                    label: v.displayName,
                    value: v.field,
                  }))}
                />

                {variable && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "12px",
                      background: "#e6f7ff",
                      borderRadius: "8px",
                      border: "1px solid #91d5ff",
                      textAlign: "center",
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ display: "block", fontSize: "11px" }}
                    >
                      {t("thresholds.currentValue")}
                    </Text>
                    <Text strong style={{ fontSize: "18px", color: "#0050b3" }}>
                      {currentMetric.value} {currentMetric.unit}
                    </Text>
                  </div>
                )}
              </div>

              {variable && noConfigFound && (
                <Alert
                  type="info"
                  showIcon
                  message={t("thresholds.messages.noConfigFound") || "Sin configuración"}
                  description={
                    t("thresholds.messages.noConfigFoundDescription") ||
                    "No hay umbrales configurados para esta variable. Puedes crear una nueva configuración a continuación."
                  }
                />
              )}

              <div>
                <Text strong>{t("thresholds.maxGaugeLimit")}</Text>
                <InputNumber
                  style={{ width: "100%", marginTop: 8 }}
                  value={max}
                  onChange={(v) => setMax(v)}
                  min={0}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "Delete",
                      "ArrowLeft",
                      "ArrowRight",
                      "Enter",
                    ];
                    const isNumberOrDot = /^[0-9.]$/.test(e.key);
                    if (
                      !allowedKeys.includes(e.key) &&
                      !isNumberOrDot &&
                      !e.ctrlKey &&
                      !e.metaKey
                    ) {
                      e.preventDefault();
                      message.warning(
                        t("thresholds.messages.invalidInputWarning"),
                        6,
                      );
                    }
                  }}
                  onBlur={() => {
                    if (max === null || max === undefined || max <= 0) {
                      setMax(1);
                    }
                  }}
                />
              </div>

              <Divider orientation="left">
                {t("thresholds.thresholdsLabel")}
              </Divider>

              {thresholds.map((tItem, index) => (
                <Row
                  key={index}
                  gutter={8}
                  style={{ marginBottom: 8 }}
                  align="middle"
                >
                  <Col span={10}>
                    <InputNumber
                      placeholder={t("thresholds.valuePlaceholder")}
                      value={tItem.value}
                      onChange={(v) => {
                        const newT = [...thresholds];
                        newT[index].value = v !== null ? Number(v) : null;
                        setThresholds(newT);
                      }}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col span={4}>
                    <input
                      type="color"
                      value={tItem.color}
                      onChange={(e) => {
                        const newT = [...thresholds];
                        newT[index].color = e.target.value;
                        setThresholds(newT);
                      }}
                      style={{
                        width: "100%",
                        height: "32px",
                        cursor: "pointer",
                        border: "1px solid #d9d9d9",
                        padding: 0,
                        background: "none",
                      }}
                    />
                  </Col>
                  <Col span={10}>
                    <Button
                      danger
                      onClick={() =>
                        setThresholds(thresholds.filter((_, i) => i !== index))
                      }
                      block
                    >
                      {t("thresholds.delete")}
                    </Button>
                  </Col>
                </Row>
              ))}

              <Button
                type="dashed"
                onClick={() =>
                  setThresholds([
                    ...thresholds,
                    { value: max, color: "#1890ff" },
                  ])
                }
                block
                disabled={!device || !variable}
              >
                {t("thresholds.addThresholds")}
              </Button>
            </Space>
          </Spin>
        </Col>

        <Col xs={24} md={14} style={{ textAlign: "center" }}>
          {variable ? (
            <div style={{ height: "auto" }}>
              <Gauge {...gaugeConfig} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: "-20px",
                }}
              >
                <Text type="secondary">
                  {t("thresholds.configuring")} {device?.name}
                </Text>
                {(currentMetric?.time ||
                  currentMetric?._time ||
                  currentMetric?.timestamp) && (
                  <Text type="secondary" strong style={{ marginTop: 4 }}>
                    {dayjs(
                      currentMetric.time ||
                        currentMetric._time ||
                        currentMetric.timestamp,
                    ).format("DD-MM-YYYY HH:mm:ss")}
                  </Text>
                )}

                <Row
                  gutter={16}
                  style={{ marginTop: 24, width: "80%", maxWidth: "400px" }}
                >
                  <Col span={8}>
                    <Button
                      block
                      size="large"
                      onClick={handleReset}
                      style={{
                        height: "45px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      {t("thresholds.reset")}
                    </Button>
                  </Col>
                  <Col span={8}>
                    <Button
                      type="primary"
                      size="large"
                      block
                      onClick={handleSaveConfig}
                      disabled={thresholds.length === 0}
                      style={{
                        height: "45px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      {t("thresholds.save")}
                    </Button>
                  </Col>
                  {existingConfig?.uuid && (
                    <Col span={8}>
                      <Button
                        danger
                        size="large"
                        block
                        onClick={() => {
                          Modal.confirm({
                            title: "¿Está seguro de eliminar esta configuración?",
                            content: "Esta acción restaurará el gráfico a su estado genérico.",
                            okText: "Eliminar",
                            okType: "danger",
                            cancelText: "Cancelar",
                            onOk: async () => {
                              try {
                                await req.delete(`/setting-threshold/${existingConfig.uuid}`);
                                message.success("Configuración eliminada correctamente");
                                setExistingConfig(null);
                                setThresholds([]);
                                setMax(100);
                              } catch (error) {
                                message.error("Error al eliminar la configuración");
                              }
                            },
                          });
                        }}
                        style={{
                          height: "45px",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        }}
                      >
                        {t("thresholds.delete")}
                      </Button>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: "100px 0",
                background: "#fafafa",
                borderRadius: 8,
                border: "1px dashed #d9d9d9",
              }}
            >
              <Text type="secondary">{t("thresholds.emptyStateText")}</Text>
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};
